<template>
  <div class="rich-editor" :class="{ disabled, 'has-error': error }">
    <div v-if="editor" class="toolbar" role="toolbar" :aria-label="i18n.toolbar">
      <div class="toolbar-row toolbar-row-primary">
        <div class="toolbar-group">
          <select
            class="toolbar-select"
            :value="headingValue"
            :disabled="isInactive"
            :aria-label="i18n.textStyle"
            @change="setHeading($event.target.value)"
          >
            <option value="paragraph">{{ i18n.paragraph }}</option>
            <option v-for="level in 6" :key="level" :value="level">
              {{ i18n.heading }} {{ level }}
            </option>
          </select>
        </div>

        <div class="toolbar-group toolbar-formats">
          <button
            v-for="format in textFormats"
            :key="format.name"
            class="toolbar-button toolbar-format-button"
            :class="[{ active: editor.isActive(format.name) }, format.className]"
            :disabled="isInactive"
            type="button"
            :title="format.title"
            @mousedown.prevent="toggleTextFormat(format.name)"
          >
            {{ format.label }}
          </button>
          <button
            class="toolbar-button toolbar-format-button"
            :class="{ active: isSmallCapsActive }"
            :disabled="isInactive"
            type="button"
            :title="i18n.smallCaps"
            @mousedown.prevent="toggleSmallCaps"
          >
            Aa
          </button>
          <button
            class="toolbar-button toolbar-icon-button"
            :disabled="isInactive || !editor.can().undo()"
            type="button"
            :title="i18n.undo"
            @mousedown.prevent="run('undo')"
          >
            <OurSvgIcon :src="icons.Undo" size="2xs" color="gray" />
          </button>
          <button
            class="toolbar-button toolbar-icon-button"
            :disabled="isInactive || !editor.can().redo()"
            type="button"
            :title="i18n.redo"
            @mousedown.prevent="run('redo')"
          >
            <OurSvgIcon :src="icons.Redo" size="2xs" color="gray" />
          </button>
        </div>
      </div>

      <div class="toolbar-row toolbar-row-settings">
        <div class="toolbar-group">
          <details class="toolbar-dropdown" :class="{ disabled: isInactive }">
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.font }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <label class="toolbar-field">
                <span>{{ i18n.fontSize }}</span>
                <select :value="fontSizeValue" :disabled="isInactive" @change="setFontSize">
                  <option value="">{{ i18n.default }}</option>
                  <option v-for="size in fontSizes" :key="size" :value="size">{{ size }}</option>
                </select>
              </label>

              <label class="toolbar-field">
                <span>{{ i18n.fontFamily }}</span>
                <select :value="fontFamilyValue" :disabled="isInactive" @change="setFontFamily">
                  <option value="">{{ i18n.default }}</option>
                  <option v-for="font in fontFamilyOptions" :key="font.value" :value="font.value">
                    {{ font.label }}
                  </option>
                </select>
              </label>

              <label class="toolbar-field">
                <span>{{ i18n.letterSpacing }}</span>
                <select
                  :value="letterSpacingValue"
                  :disabled="isInactive"
                  @change="setLetterSpacing"
                >
                  <option value="">{{ i18n.default }}</option>
                  <option
                    v-for="spacing in letterSpacings"
                    :key="spacing.value"
                    :value="spacing.value"
                  >
                    {{ spacing.label }}
                  </option>
                </select>
              </label>
            </div>
          </details>
        </div>

        <div class="toolbar-group">
          <details class="toolbar-dropdown" :class="{ disabled: isInactive }">
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.spacing }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <div class="toolbar-menu-grid">
                <button
                  class="toolbar-menu-item"
                  :disabled="isInactive || blockIndent <= 0"
                  type="button"
                  @mousedown.prevent="changeIndent(-1)"
                >
                  {{ i18n.decreaseIndent }}
                </button>
                <button
                  class="toolbar-menu-item"
                  :disabled="isInactive || blockIndent >= 8"
                  type="button"
                  @mousedown.prevent="changeIndent(1)"
                >
                  {{ i18n.increaseIndent }}
                </button>
              </div>

              <label class="toolbar-field">
                <span>{{ i18n.lineHeight }}</span>
                <select :value="lineHeightValue" :disabled="isInactive" @change="setLineHeight">
                  <option value="">{{ i18n.default }}</option>
                  <option v-for="height in lineHeights" :key="height" :value="height">
                    {{ height }}
                  </option>
                </select>
              </label>

              <label class="toolbar-field">
                <span>{{ i18n.paragraphSpacing }}</span>
                <select
                  :value="paragraphSpacingValue"
                  :disabled="isInactive"
                  @change="setParagraphSpacing"
                >
                  <option value="">{{ i18n.default }}</option>
                  <option v-for="spacing in paragraphSpacings" :key="spacing" :value="spacing">
                    {{ spacing }}
                  </option>
                </select>
              </label>

              <label class="toolbar-field">
                <span>{{ i18n.textDirection }}</span>
                <select :value="directionValue" :disabled="isInactive" @change="setDirection">
                  <option value="">{{ i18n.automatic }}</option>
                  <option value="ltr">{{ i18n.ltr }}</option>
                  <option value="rtl">{{ i18n.rtl }}</option>
                </select>
              </label>
            </div>
          </details>
        </div>

        <div class="toolbar-group toolbar-formats">
          <button
            v-for="alignment in alignments"
            :key="alignment.value"
            class="toolbar-button toolbar-icon-button"
            :class="{ active: alignmentValue === alignment.value }"
            :disabled="isInactive"
            type="button"
            :title="alignment.label"
            @mousedown.prevent="setTextAlign(alignment.value)"
          >
            <OurSvgIcon :src="alignment.icon" size="xs" color="black" />
          </button>
        </div>

        <div class="toolbar-group">
          <select
            class="toolbar-select"
            :value="listValue"
            :disabled="isInactive"
            :aria-label="i18n.listType"
            @change="setList($event.target.value)"
          >
            <option value="none">{{ i18n.noList }}</option>
            <option v-for="list in lists" :key="list.value" :value="list.value">
              {{ list.label }}
            </option>
          </select>
        </div>
      </div>

      <div class="toolbar-row toolbar-row-actions">
        <div class="toolbar-group">
          <details class="toolbar-dropdown" :class="{ disabled: isInactive }">
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.blocks }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <div class="toolbar-menu-grid toolbar-menu-grid-last">
                <button
                  class="toolbar-menu-item"
                  :class="{ active: editor.isActive('blockquote') }"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="run('toggleBlockquote')"
                >
                  <span>{{ i18n.blockquote }}</span>
                  <span v-if="editor.isActive('blockquote')">✓</span>
                </button>
                <button
                  class="toolbar-menu-item"
                  :class="{ active: editor.isActive('codeBlock') }"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="run('toggleCodeBlock')"
                >
                  <span>{{ i18n.codeBlock }}</span>
                  <span v-if="editor.isActive('codeBlock')">✓</span>
                </button>
                <button
                  class="toolbar-menu-item"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="run('setHorizontalRule')"
                >
                  {{ i18n.horizontalRule }}
                </button>
              </div>
            </div>
          </details>
        </div>

        <div class="toolbar-group">
          <details
            class="toolbar-dropdown toolbar-dropdown-right"
            :class="{ disabled: isInactive }"
          >
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.colors }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <label class="toolbar-color-field">
                <span>{{ i18n.textColor }}</span>
                <input
                  class="color-input"
                  type="color"
                  :disabled="isInactive"
                  :value="textColor"
                  :aria-label="i18n.textColor"
                  @input="setColor($event.target.value)"
                />
              </label>
              <button
                class="toolbar-menu-item"
                :disabled="isInactive || !hasTextColor"
                type="button"
                @mousedown.prevent="clearColor"
              >
                {{ i18n.resetTextColor }}
              </button>
              <label class="toolbar-color-field">
                <span>{{ i18n.highlightColor }}</span>
                <input
                  class="color-input"
                  type="color"
                  :disabled="isInactive"
                  :value="highlightColor"
                  :aria-label="i18n.highlightColor"
                  @input="setHighlight($event.target.value)"
                />
              </label>
              <button
                class="toolbar-menu-item"
                :disabled="isInactive || !editor.isActive('highlight')"
                type="button"
                @mousedown.prevent="clearHighlight"
              >
                {{ i18n.resetHighlight }}
              </button>
              <button
                class="toolbar-menu-item toolbar-menu-danger"
                :disabled="isInactive"
                type="button"
                @mousedown.prevent="clearFormatting"
              >
                {{ i18n.clearFormatting }}
              </button>
            </div>
          </details>
        </div>

        <div class="toolbar-group">
          <details
            class="toolbar-dropdown toolbar-dropdown-right"
            :class="{ disabled: isInactive }"
          >
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.insert }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <div class="toolbar-menu-grid toolbar-menu-grid-last">
                <button
                  class="toolbar-menu-item"
                  :class="{ active: editor.isActive('link') }"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="setLink"
                >
                  {{ i18n.link }}
                </button>
                <button
                  class="toolbar-menu-item"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="selectImage"
                >
                  {{ i18n.image }}
                </button>
                <button
                  class="toolbar-menu-item"
                  :class="{ active: editor.isActive('legacyVideo') }"
                  :disabled="isInactive"
                  type="button"
                  @mousedown.prevent="setVideo"
                >
                  {{ i18n.video }}
                </button>
              </div>
            </div>
          </details>
        </div>

        <div class="toolbar-group">
          <details
            class="toolbar-dropdown toolbar-dropdown-right"
            :class="{ disabled: isInactive }"
          >
            <summary class="toolbar-select" @click="onDropdownClick">
              <span>{{ i18n.table }}</span>
              <OurSvgIcon :src="icons.AngleDown" size="2xs" color="gray" />
            </summary>
            <div class="toolbar-menu toolbar-menu-wide">
              <button
                class="toolbar-menu-item toolbar-menu-primary"
                :disabled="isInactive"
                type="button"
                @mousedown.prevent="insertTable"
              >
                {{ i18n.insertTable }}
              </button>
              <div
                v-if="editor.isActive('table')"
                class="toolbar-menu-grid toolbar-menu-grid-spaced"
              >
                <button
                  v-for="action in tableActions"
                  :key="action.command"
                  class="toolbar-menu-item"
                  :disabled="isInactive"
                  type="button"
                  :title="action.title"
                  @mousedown.prevent="run(action.command)"
                >
                  {{ action.title }}
                </button>
              </div>
            </div>
          </details>
        </div>
      </div>
    </div>

    <editor-content class="editor-content rich-content" :editor="editor" />

    <input
      ref="imageInput"
      class="image-input"
      type="file"
      accept="image/*"
      @change="onImageSelected"
    />

    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script>
import { Editor, EditorContent } from "@tiptap/vue-2";
import StarterKit from "@tiptap/starter-kit";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import TaskItem from "@tiptap/extension-task-item";
import TaskList from "@tiptap/extension-task-list";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import Underline from "@tiptap/extension-underline";
import ControlledTextStyle from "./extensions/ControlledTextStyle";
import LegacyVideo from "./extensions/LegacyVideo";
import normalizeLegacyHtml from "./utils/normalizeLegacyHtml";
import I18nServiceDefault from "@/services/_bit/I18nService";

export default {
  name: "OurRichEditor",

  components: {
    EditorContent,
  },

  props: {
    value: {
      type: String,
      default: "",
    },
    placeholder: {
      type: String,
      default: "",
    },
    editorToolbar: {
      type: Array,
      default: () => [],
    },
    useCustomImageHandler: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      editor: null,
      textColor: "#111827",
      highlightColor: "#fff59d",
      fontSizes: ["12px", "14px", "16px", "18px", "20px", "24px", "30px", "36px"],
      fontFamilies: [
        { value: "Arial, sans-serif", label: "Arial" },
        { value: "Georgia, serif", label: "Georgia" },
        { value: "'Times New Roman', serif", label: "Times New Roman" },
        { value: "Verdana, sans-serif", label: "Verdana" },
        { value: "monospace", labelKey: "fontMonospace" },
      ],
      lineHeights: ["1", "1.25", "1.5", "1.75", "2"],
      paragraphSpacings: ["0", "0.5rem", "1rem", "1.5rem", "2rem"],
    };
  },

  computed: {
    icons: () => ({
      AngleDown: require("@/components/_bit/IconSvg/icons/Angle-down.svg"),
      Redo: require("@/components/_bit/IconSvg/icons/Redo.svg"),
      Undo: require("@/components/_bit/IconSvg/icons/Undo.svg"),
      AlignLeft: require("@/components/_bit/IconSvg/icons/Align-left.svg"),
      AlignCenter: require("@/components/_bit/IconSvg/icons/Align-center.svg"),
      AlignRight: require("@/components/_bit/IconSvg/icons/Align-right.svg"),
      AlignJustify: require("@/components/_bit/IconSvg/icons/Align-justify.svg"),
    }),

    getTranslation() {
      const translate = (path) => this.$t(path);
      const options = { componentName: this.$options.name };

      return new I18nServiceDefault(translate, options).getTranslation;
    },

    i18n() {
      return {
        toolbar: this.getTranslation("toolbar"),
        paragraph: this.getTranslation("paragraph"),
        heading: this.getTranslation("heading"),
        textStyle: this.getTranslation("textStyle"),
        listType: this.getTranslation("listType"),
        font: this.getTranslation("font"),
        fontSize: this.getTranslation("fontSize"),
        fontFamily: this.getTranslation("fontFamily"),
        letterSpacing: this.getTranslation("letterSpacing"),
        default: this.getTranslation("default"),
        spacing: this.getTranslation("spacing"),
        decreaseIndent: this.getTranslation("decreaseIndent"),
        increaseIndent: this.getTranslation("increaseIndent"),
        lineHeight: this.getTranslation("lineHeight"),
        paragraphSpacing: this.getTranslation("paragraphSpacing"),
        textDirection: this.getTranslation("textDirection"),
        automatic: this.getTranslation("automatic"),
        ltr: this.getTranslation("ltr"),
        rtl: this.getTranslation("rtl"),
        undo: this.getTranslation("undo"),
        redo: this.getTranslation("redo"),
        noList: this.getTranslation("noList"),
        blocks: this.getTranslation("blocks"),
        blockquote: this.getTranslation("blockquote"),
        codeBlock: this.getTranslation("codeBlock"),
        horizontalRule: this.getTranslation("horizontalRule"),
        colors: this.getTranslation("colors"),
        textColor: this.getTranslation("textColor"),
        resetTextColor: this.getTranslation("resetTextColor"),
        highlightColor: this.getTranslation("highlightColor"),
        resetHighlight: this.getTranslation("resetHighlight"),
        clearFormatting: this.getTranslation("clearFormatting"),
        insert: this.getTranslation("insert"),
        link: this.getTranslation("link"),
        image: this.getTranslation("image"),
        video: this.getTranslation("video"),
        table: this.getTranslation("table"),
        insertTable: this.getTranslation("insertTable"),
        smallCaps: this.getTranslation("smallCaps"),
        bold: this.getTranslation("bold"),
        italic: this.getTranslation("italic"),
        underline: this.getTranslation("underline"),
        strikethrough: this.getTranslation("strikethrough"),
        inlineCode: this.getTranslation("inlineCode"),
        subscript: this.getTranslation("subscript"),
        superscript: this.getTranslation("superscript"),
        spacingTight: this.getTranslation("spacingTight"),
        spacingNormal: this.getTranslation("spacingNormal"),
        spacingWide: this.getTranslation("spacingWide"),
        spacingExtraWide: this.getTranslation("spacingExtraWide"),
        alignLeft: this.getTranslation("alignLeft"),
        alignCenter: this.getTranslation("alignCenter"),
        alignRight: this.getTranslation("alignRight"),
        alignJustify: this.getTranslation("alignJustify"),
        bulletList: this.getTranslation("bulletList"),
        orderedList: this.getTranslation("orderedList"),
        taskList: this.getTranslation("taskList"),
        addColumnBefore: this.getTranslation("addColumnBefore"),
        addColumnAfter: this.getTranslation("addColumnAfter"),
        deleteColumn: this.getTranslation("deleteColumn"),
        addRowBefore: this.getTranslation("addRowBefore"),
        addRowAfter: this.getTranslation("addRowAfter"),
        deleteRow: this.getTranslation("deleteRow"),
        mergeOrSplit: this.getTranslation("mergeOrSplit"),
        toggleHeaderRow: this.getTranslation("toggleHeaderRow"),
        deleteTable: this.getTranslation("deleteTable"),
        promptLink: this.getTranslation("promptLink"),
        promptImage: this.getTranslation("promptImage"),
        promptVideo: this.getTranslation("promptVideo"),
        fontMonospace: this.getTranslation("fontMonospace"),
      };
    },

    textFormats() {
      return [
        { name: "bold", label: "B", title: this.i18n.bold, className: "font-bold" },
        { name: "italic", label: "I", title: this.i18n.italic, className: "italic" },
        { name: "underline", label: "U", title: this.i18n.underline, className: "underline" },
        {
          name: "strike",
          label: "S",
          title: this.i18n.strikethrough,
          className: "line-through",
        },
        { name: "code", label: "</>", title: this.i18n.inlineCode, className: "font-mono" },
        { name: "subscript", label: "X₂", title: this.i18n.subscript, className: "" },
        { name: "superscript", label: "X²", title: this.i18n.superscript, className: "" },
      ];
    },

    letterSpacings() {
      return [
        { value: "-0.025em", label: this.i18n.spacingTight },
        { value: "0", label: this.i18n.spacingNormal },
        { value: "0.05em", label: this.i18n.spacingWide },
        { value: "0.1em", label: this.i18n.spacingExtraWide },
      ];
    },

    fontFamilyOptions() {
      return this.fontFamilies.map((font) => ({
        value: font.value,
        label: font.labelKey ? this.i18n[font.labelKey] : font.label,
      }));
    },

    alignments() {
      return [
        { value: "left", label: this.i18n.alignLeft, icon: this.icons.AlignLeft },
        { value: "center", label: this.i18n.alignCenter, icon: this.icons.AlignCenter },
        { value: "right", label: this.i18n.alignRight, icon: this.icons.AlignRight },
        { value: "justify", label: this.i18n.alignJustify, icon: this.icons.AlignJustify },
      ];
    },

    lists() {
      return [
        { value: "bulletList", label: this.i18n.bulletList, command: "toggleBulletList" },
        { value: "orderedList", label: this.i18n.orderedList, command: "toggleOrderedList" },
        { value: "taskList", label: this.i18n.taskList, command: "toggleTaskList" },
      ];
    },

    tableActions() {
      return [
        { command: "addColumnBefore", title: this.i18n.addColumnBefore },
        { command: "addColumnAfter", title: this.i18n.addColumnAfter },
        { command: "deleteColumn", title: this.i18n.deleteColumn },
        { command: "addRowBefore", title: this.i18n.addRowBefore },
        { command: "addRowAfter", title: this.i18n.addRowAfter },
        { command: "deleteRow", title: this.i18n.deleteRow },
        { command: "mergeOrSplit", title: this.i18n.mergeOrSplit },
        { command: "toggleHeaderRow", title: this.i18n.toggleHeaderRow },
        { command: "deleteTable", title: this.i18n.deleteTable },
      ];
    },

    isInactive() {
      return this.disabled || this.readonly;
    },

    headingValue() {
      if (!this.editor) return "paragraph";

      for (let level = 1; level <= 6; level += 1) {
        if (this.editor.isActive("heading", { level })) return String(level);
      }

      return "paragraph";
    },

    alignmentValue() {
      if (!this.editor) return "left";

      const activeAlignment = this.alignments.find(({ value }) =>
        this.editor.isActive({ textAlign: value })
      );

      return activeAlignment?.value || "left";
    },

    listValue() {
      if (!this.editor) return "none";

      const activeList = this.lists.find(({ value }) => this.editor.isActive(value));

      return activeList?.value || "none";
    },

    textStyleAttributes() {
      return this.editor?.getAttributes("textStyle") || {};
    },

    fontSizeValue() {
      return this.textStyleAttributes.fontSize || "";
    },

    fontFamilyValue() {
      return this.textStyleAttributes.fontFamily || "";
    },

    letterSpacingValue() {
      return this.textStyleAttributes.letterSpacing || "";
    },

    isSmallCapsActive() {
      return this.textStyleAttributes.fontVariant === "small-caps";
    },

    hasTextColor() {
      return !!this.textStyleAttributes.color;
    },

    activeBlockType() {
      return this.editor?.isActive("heading") ? "heading" : "paragraph";
    },

    activeBlockAttributes() {
      return this.editor?.getAttributes(this.activeBlockType) || {};
    },

    blockIndent() {
      return Number(this.activeBlockAttributes.indent || 0);
    },

    lineHeightValue() {
      return this.activeBlockAttributes.lineHeight || "";
    },

    paragraphSpacingValue() {
      return this.activeBlockAttributes.paragraphSpacing || "";
    },

    directionValue() {
      return this.activeBlockAttributes.direction || "";
    },
  },

  watch: {
    value(value) {
      if (!this.editor) return;

      const normalizedValue = normalizeLegacyHtml(value);

      if (this.editor.getHTML() !== normalizedValue) {
        this.editor.commands.setContent(normalizedValue, false);
      }
    },

    isInactive(value) {
      if (this.editor) this.editor.setEditable(!value);
    },
  },

  mounted() {
    this.editor = new Editor({
      content: normalizeLegacyHtml(this.value),
      editable: !this.isInactive,
      extensions: [
        StarterKit,
        Underline,
        TextStyle,
        ControlledTextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        Subscript,
        Superscript,
        TextAlign.configure({
          types: ["heading", "paragraph"],
          defaultAlignment: "left",
        }),
        Image.configure({ allowBase64: false }),
        Link.configure({
          autolink: true,
          linkOnPaste: true,
          openOnClick: false,
        }),
        Placeholder.configure({ placeholder: this.placeholder }),
        TaskList,
        TaskItem.configure({ nested: true }),
        Table.configure({ resizable: true }),
        TableRow,
        TableHeader,
        TableCell,
        LegacyVideo,
      ],
      onUpdate: ({ editor }) => {
        this.$emit("input", editor.getHTML());
      },
      onFocus: () => {
        this.$emit("focus");
      },
      onBlur: () => {
        this.$emit("blur");
      },
    });

    document.addEventListener("click", this.closeDropdownsOnOutsideClick);
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeDropdownsOnOutsideClick);

    if (this.editor) this.editor.destroy();
  },

  methods: {
    run(command) {
      this.editor.chain().focus()[command]().run();
    },

    onDropdownClick(event) {
      if (this.isInactive) {
        event.preventDefault();

        return;
      }

      const currentDropdown = event.currentTarget.parentElement;

      if (!currentDropdown.open) {
        this.$el.querySelectorAll(".toolbar-dropdown[open]").forEach((dropdown) => {
          dropdown.removeAttribute("open");
        });
      }
    },

    closeDropdownsOnOutsideClick(event) {
      if (event.target.closest(".toolbar-dropdown")) return;

      this.$el.querySelectorAll(".toolbar-dropdown[open]").forEach((dropdown) => {
        dropdown.removeAttribute("open");
      });
    },

    toggleTextFormat(format) {
      const command = `toggle${format.charAt(0).toUpperCase()}${format.slice(1)}`;

      this.run(command);
    },

    setTextStyleAttribute(attribute, value) {
      this.editor
        .chain()
        .focus()
        .setMark("textStyle", { [attribute]: value || null })
        .removeEmptyTextStyle()
        .run();
    },

    setFontSize(event) {
      this.setTextStyleAttribute("fontSize", event.target.value);
    },

    setFontFamily(event) {
      this.setTextStyleAttribute("fontFamily", event.target.value);
    },

    setLetterSpacing(event) {
      this.setTextStyleAttribute("letterSpacing", event.target.value);
    },

    toggleSmallCaps() {
      this.setTextStyleAttribute("fontVariant", this.isSmallCapsActive ? null : "small-caps");
    },

    setHeading(value) {
      const chain = this.editor.chain().focus();

      if (value === "paragraph") {
        chain.setParagraph().run();

        return;
      }

      chain.toggleHeading({ level: Number(value) }).run();
    },

    setTextAlign(alignment) {
      const chain = this.editor.chain().focus();

      if (alignment === "left") {
        chain.unsetTextAlign().run();

        return;
      }

      chain.setTextAlign(alignment).run();
    },

    updateBlockAttribute(attribute, value) {
      this.editor
        .chain()
        .focus()
        .updateAttributes(this.activeBlockType, { [attribute]: value || null })
        .run();
    },

    changeIndent(change) {
      const indent = Math.min(8, Math.max(0, this.blockIndent + change));

      this.updateBlockAttribute("indent", indent);
    },

    setLineHeight(event) {
      this.updateBlockAttribute("lineHeight", event.target.value);
    },

    setParagraphSpacing(event) {
      this.updateBlockAttribute("paragraphSpacing", event.target.value);
    },

    setDirection(event) {
      this.updateBlockAttribute("direction", event.target.value);
    },

    setList(value) {
      const activeList = this.lists.find(({ value: listValue }) => this.editor.isActive(listValue));

      if (value === "none") {
        if (activeList) this.run(activeList.command);

        return;
      }

      const selectedList = this.lists.find(({ value: listValue }) => listValue === value);

      if (selectedList) this.run(selectedList.command);
    },

    setColor(color) {
      this.textColor = color;
      this.editor.chain().focus().setColor(color).run();
    },

    clearColor() {
      this.editor.chain().focus().unsetColor().run();
    },

    setHighlight(color) {
      this.highlightColor = color;
      this.editor.chain().focus().setHighlight({ color }).run();
    },

    clearHighlight() {
      this.editor.chain().focus().unsetHighlight().run();
    },

    clearFormatting() {
      this.editor.chain().focus().unsetAllMarks().clearNodes().run();
    },

    setLink() {
      const currentUrl = this.editor.getAttributes("link").href || "";
      const url = window.prompt(this.i18n.promptLink, currentUrl);

      if (url === null) return;

      if (!url.trim()) {
        this.editor.chain().focus().extendMarkRange("link").unsetLink().run();

        return;
      }

      this.editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
    },

    selectImage() {
      if (this.useCustomImageHandler) {
        this.$refs.imageInput.click();

        return;
      }

      const url = window.prompt(this.i18n.promptImage);

      if (url?.trim()) {
        this.editor.chain().focus().setImage({ src: url.trim() }).run();
      }
    },

    onImageSelected(event) {
      const [file] = event.target.files;

      if (!file) return;

      const cursorLocation = this.editor.state.selection.from;

      const resetUploader = () => {
        if (this.$refs.imageInput) this.$refs.imageInput.value = "";
      };

      const editorAdapter = {
        insertEmbed: (position, type, url) => {
          if (type !== "image" || !url) return;

          this.editor
            .chain()
            .focus()
            .insertContentAt(position, {
              type: "image",
              attrs: { src: url },
            })
            .run();
        },
      };

      this.$emit("image-added", file, editorAdapter, cursorLocation, resetUploader);
    },

    setVideo() {
      const url = window.prompt(this.i18n.promptVideo);

      if (!url?.trim()) return;

      this.editor.chain().focus().setVideo({ src: url.trim() }).run();
    },

    insertTable() {
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },
  },
};
</script>

<i18n>
en:
  toolbar: "Rich text editor toolbar"
  paragraph: "Paragraph"
  heading: "Heading"
  textStyle: "Text style"
  listType: "List type"
  font: "Font"
  fontSize: "Font size"
  fontFamily: "Font family"
  letterSpacing: "Letter spacing"
  default: "Default"
  spacing: "Spacing"
  decreaseIndent: "Decrease indent"
  increaseIndent: "Increase indent"
  lineHeight: "Line height"
  paragraphSpacing: "Paragraph spacing"
  textDirection: "Text direction"
  automatic: "Automatic"
  ltr: "Left to right"
  rtl: "Right to left"
  undo: "Undo"
  redo: "Redo"
  noList: "No list"
  blocks: "Blocks"
  blockquote: "Blockquote"
  codeBlock: "Code block"
  horizontalRule: "Horizontal rule"
  colors: "Colors"
  textColor: "Text color"
  resetTextColor: "Reset text color"
  highlightColor: "Highlight color"
  resetHighlight: "Reset highlight"
  clearFormatting: "Clear all formatting"
  insert: "Insert"
  link: "Link"
  image: "Image"
  video: "Video"
  table: "Table"
  insertTable: "Insert 3 × 3 table"
  smallCaps: "Small caps"
  bold: "Bold"
  italic: "Italic"
  underline: "Underline"
  strikethrough: "Strikethrough"
  inlineCode: "Inline code"
  subscript: "Subscript"
  superscript: "Superscript"
  spacingTight: "Tight"
  spacingNormal: "Normal"
  spacingWide: "Wide"
  spacingExtraWide: "Extra wide"
  alignLeft: "Align left"
  alignCenter: "Align center"
  alignRight: "Align right"
  alignJustify: "Justify"
  bulletList: "Bullet list"
  orderedList: "Ordered list"
  taskList: "Task list"
  addColumnBefore: "Add column before"
  addColumnAfter: "Add column after"
  deleteColumn: "Delete column"
  addRowBefore: "Add row before"
  addRowAfter: "Add row after"
  deleteRow: "Delete row"
  mergeOrSplit: "Merge or split cells"
  toggleHeaderRow: "Toggle header row"
  deleteTable: "Delete table"
  promptLink: "Enter a link URL"
  promptImage: "Enter an image URL"
  promptVideo: "Enter a video embed URL"
  fontMonospace: "Monospace"
ua:
  toolbar: "Панель інструментів текстового редактора"
  paragraph: "Абзац"
  heading: "Заголовок"
  textStyle: "Стиль тексту"
  listType: "Тип списку"
  font: "Шрифт"
  fontSize: "Розмір шрифту"
  fontFamily: "Сімейство шрифтів"
  letterSpacing: "Міжлітерний інтервал"
  default: "За замовчуванням"
  spacing: "Інтервали"
  decreaseIndent: "Зменшити відступ"
  increaseIndent: "Збільшити відступ"
  lineHeight: "Міжрядковий інтервал"
  paragraphSpacing: "Інтервал абзацу"
  textDirection: "Напрям тексту"
  automatic: "Автоматично"
  ltr: "Зліва направо"
  rtl: "Справа наліво"
  undo: "Скасувати"
  redo: "Повторити"
  noList: "Без списку"
  blocks: "Блоки"
  blockquote: "Цитата"
  codeBlock: "Блок коду"
  horizontalRule: "Горизонтальна лінія"
  colors: "Кольори"
  textColor: "Колір тексту"
  resetTextColor: "Скинути колір тексту"
  highlightColor: "Колір виділення"
  resetHighlight: "Скинути виділення"
  clearFormatting: "Очистити форматування"
  insert: "Вставити"
  link: "Посилання"
  image: "Зображення"
  video: "Відео"
  table: "Таблиця"
  insertTable: "Вставити таблицю 3 × 3"
  smallCaps: "Малі прописні"
  bold: "Жирний"
  italic: "Курсив"
  underline: "Підкреслення"
  strikethrough: "Закреслення"
  inlineCode: "Код"
  subscript: "Нижній індекс"
  superscript: "Верхній індекс"
  spacingTight: "Щільний"
  spacingNormal: "Звичайний"
  spacingWide: "Широкий"
  spacingExtraWide: "Дуже широкий"
  alignLeft: "Ліворуч"
  alignCenter: "По центру"
  alignRight: "Праворуч"
  alignJustify: "За шириною"
  bulletList: "Маркований список"
  orderedList: "Нумерований список"
  taskList: "Список завдань"
  addColumnBefore: "Додати стовпець перед"
  addColumnAfter: "Додати стовпець після"
  deleteColumn: "Видалити стовпець"
  addRowBefore: "Додати рядок перед"
  addRowAfter: "Додати рядок після"
  deleteRow: "Видалити рядок"
  mergeOrSplit: "Об'єднати або розділити"
  toggleHeaderRow: "Рядок заголовка"
  deleteTable: "Видалити таблицю"
  promptLink: "Введіть URL посилання"
  promptImage: "Введіть URL зображення"
  promptVideo: "Введіть URL відео"
  fontMonospace: "Моноширинний"
</i18n>

<style lang="postcss" scoped>
.rich-editor {
  @apply overflow-hidden rounded-lg border border-solid border-gray-300 bg-white;

  &:focus-within {
    @apply border-gray-500 ring-4 ring-gray-600 ring-opacity-15;
  }

  &.disabled {
    @apply bg-gray-100 opacity-75;
  }

  &.has-error {
    @apply border-red-300;
  }
}

.toolbar {
  @apply relative z-10 flex flex-col gap-1 border-b border-gray-200 bg-gray-50 p-2.5;
}

.toolbar-row {
  @apply flex flex-nowrap items-center gap-1 overflow-x-auto;
}

.toolbar-group {
  @apply flex shrink-0 items-center gap-0.5;
}

.toolbar-formats {
  @apply gap-px;
}

.toolbar-button,
.toolbar-select {
  @apply min-h-[2.25rem] rounded-md border border-gray-200 bg-white px-2.5 text-xs font-medium leading-none text-gray-700 shadow-sm outline-none;
  @apply transition-colors hover:border-gray-400 hover:bg-gray-50;
  @apply focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-opacity-20;
}

.toolbar-button {
  @apply inline-flex cursor-pointer items-center justify-center;

  &.active {
    @apply border-accent bg-accent bg-opacity-10 text-accent;
  }

  &:disabled {
    @apply cursor-not-allowed opacity-40;
  }
}

.toolbar-icon-button,
.toolbar-format-button {
  @apply min-w-[2.25rem] px-0;
}

.toolbar-icon-button:deep(.svg-icon) {
  @apply text-gray-800;
}

.toolbar-icon-button.active:deep(.svg-icon) {
  @apply text-accent;
}

.toolbar-format-button {
  @apply px-1.5;
}

.toolbar-select {
  @apply cursor-pointer py-1 pr-8;
}

.toolbar-dropdown {
  @apply static;

  &[open] > .toolbar-select {
    @apply border-accent bg-accent bg-opacity-10 text-accent shadow-none;
  }

  &.disabled {
    @apply pointer-events-none opacity-40;
  }

  summary {
    @apply inline-flex list-none items-center justify-between gap-2 pr-2.5;

    &::-webkit-details-marker {
      @apply hidden;
    }
  }
}

.toolbar-dropdown-right {
  .toolbar-menu {
    @apply left-auto right-0;
  }
}

.toolbar-menu {
  @apply absolute left-0 top-full z-20 mt-2 rounded-lg border border-gray-200 bg-white p-2.5 shadow-xl;
  max-width: calc(100vw - 2rem);
}

.toolbar-menu-wide {
  @apply w-64;
}

.toolbar-menu-grid {
  @apply mb-2 grid grid-cols-2 gap-1;
}

.toolbar-menu-grid-last {
  @apply mb-0;
}

.toolbar-menu-grid-spaced {
  @apply mb-0 mt-2 border-t border-gray-100 pt-2;
}

.toolbar-menu-item {
  @apply flex min-h-[2.25rem] w-full items-center justify-between rounded-md px-2.5 text-left text-xs text-gray-700;
  @apply hover:bg-gray-100;

  &.active {
    @apply bg-accent bg-opacity-10 text-accent;
  }

  &:disabled {
    @apply cursor-not-allowed opacity-40;
  }
}

.toolbar-menu-primary {
  @apply bg-accent bg-opacity-10 font-medium text-accent;
}

.toolbar-menu-danger {
  @apply mt-2 border-t border-gray-100 pt-2 text-red-500;
}

.toolbar-field {
  @apply mb-2 block text-xs text-gray-600 last:mb-0;

  select {
    @apply mt-1 w-full rounded-md border-gray-300 py-1.5 pl-2.5 pr-7 text-xs text-gray-700;
  }
}

.toolbar-color-field {
  @apply mb-1 flex items-center justify-between rounded-md px-2.5 py-1.5 text-xs text-gray-600;
}

.color-input {
  @apply h-6 w-8 cursor-pointer rounded border-0 bg-transparent p-0;
}

.editor-content {
  @apply bg-white;

  ::v-deep .ProseMirror {
    @apply min-h-[16rem] px-4 py-3 text-left text-base text-gray-900 outline-none;

    > * + * {
      @apply mt-3;
    }

    p.is-editor-empty:first-child::before {
      @apply pointer-events-none float-left h-0 text-gray-400;
      content: attr(data-placeholder);
    }

    h1 {
      @apply text-4xl font-bold;
    }

    h2 {
      @apply text-3xl font-bold;
    }

    h3 {
      @apply text-2xl font-semibold;
    }

    h4 {
      @apply text-xl font-semibold;
    }

    h5 {
      @apply text-lg font-semibold;
    }

    h6 {
      @apply text-base font-semibold;
    }

    ul {
      @apply list-disc pl-6;
    }

    ol {
      @apply list-decimal pl-6;
    }

    ul[data-type="taskList"] {
      @apply list-none pl-0;

      li {
        @apply flex items-start gap-2;
      }
    }

    blockquote {
      @apply border-l-4 border-gray-300 pl-4 italic text-gray-600;
    }

    pre {
      @apply overflow-x-auto rounded bg-gray-900 p-4 text-sm text-white;
    }

    a {
      @apply cursor-pointer text-blue-600 underline;
    }

    img {
      @apply inline-block max-w-full rounded;
    }

    iframe {
      @apply min-h-[20rem] w-full max-w-full;
    }

    table {
      .selectedCell::after {
        @apply pointer-events-none absolute inset-0 bg-blue-100 bg-opacity-60;
        content: "";
      }

      .column-resize-handle {
        @apply pointer-events-none absolute -bottom-px -right-0.5 top-0 w-1 bg-blue-500;
      }
    }
  }
}

.image-input {
  @apply hidden;
}

.error-message {
  @apply px-4 pb-2 text-xs font-normal text-red-500;
}

@media (max-width: 640px) {
  .toolbar {
    @apply p-2;
  }

  .toolbar-row {
    @apply overflow-x-visible;
  }

  .toolbar-group {
    @apply max-w-full;
  }

  .toolbar-row-primary {
    @apply grid grid-cols-1;

    .toolbar-formats {
      @apply flex-nowrap gap-px;
    }

    .toolbar-icon-button,
    .toolbar-format-button {
      @apply min-w-[1.75rem];
    }
  }

  .toolbar-row-settings {
    @apply grid grid-cols-[max-content_max-content] items-center gap-1;

    .toolbar-group:nth-child(3) {
      @apply col-start-1 row-start-2;

      .toolbar-icon-button {
        @apply min-w-[2rem];
      }
    }

    .toolbar-group:nth-child(4) {
      @apply col-start-2 row-start-2;

      .toolbar-select {
        @apply w-32;
      }
    }
  }

  .toolbar-row-actions {
    @apply grid grid-cols-2 gap-1;

    .toolbar-group,
    .toolbar-dropdown,
    .toolbar-select {
      @apply w-full;
    }
  }
}
</style>
